import React from 'react';
import Modal from 'react-modal';
import { FaBroadcastTower } from 'react-icons/fa';
import { channelPageUrl, streamSourceLabel } from '../../../_helpers/streamSource.js';
import { useT } from '../../../contexts/LocaleContext.jsx';
import { withStrong } from '../../../_helpers/richMessage.jsx';
import '../../../styles/partial/StreamSourceBadge.css';
import '../../../styles/partial/modal/GoLiveModal.css';

/**
 * Confirms going live. A band has at most one stream source (see
 * `_helpers/streamSource.js`), so there is nothing to pick: the caller has
 * already read `GET /public/bands/{id}/stream/source` and passes it as
 * `source` (`{ source, channelId }`, or null when none is configured).
 * `sourceUnavailable` marks a null that means "the read failed" rather than
 * "none configured", so the band is told before it commits. `onGoLive`
 * receives `source` back.
 */
// `error` is shown when going live failed (ENG-504); the modal stays open
// so the band can try again. `isSubmitting` disables Go Live meanwhile.
//
// `feed` is the SongShake feed gate (ENG-763): 'required' when the server
// needs the feed and SongShake isn't receiving it (or Go Live answered
// songshake_feed_missing), null otherwise. 'required' disables Go Live, says
// why and offers `onOpenFeedSetup`, the setup instructions.
function GoLiveModal({
  isOpen, onClose, onGoLive, source = null, sourceUnavailable = false, error = null, isSubmitting = false,
  feed = null, onOpenFeedSetup,
}) {
  const t = useT();
  const channelUrl = source ? channelPageUrl(source.source, source.channelId) : null;
  const settingsPath = t('events.goLive.settingsPath');

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel={t('events.goLive.title')}
      className="modalContent"
      overlayClassName="modalOverlay"
    >
      <h2 className="go-live-modal-title">
        <FaBroadcastTower className="go-live-modal-icon" aria-hidden="true" />
        {t('events.goLive.title')}
      </h2>

      {source ? (
        <>
          <p className="go-live-modal-hint">
            {t('events.goLive.watchHint', { provider: streamSourceLabel(source.source, t) })}
          </p>
          <div className="go-live-source" role="group" aria-label={t('events.goLive.streamSourceGroup')}>
            <span className="stream-source-type-badge">{streamSourceLabel(source.source, t)}</span>
            {channelUrl ? (
              <a
                className="go-live-source-name"
                href={channelUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {source.channelId}
              </a>
            ) : (
              <span className="go-live-source-name">{source.channelId}</span>
            )}
          </div>
        </>
      ) : sourceUnavailable ? (
        <p className="go-live-modal-hint" role="alert">
          {withStrong(t('events.goLive.sourceUnavailable'), { settingsPath })}
        </p>
      ) : (
        <p className="go-live-modal-hint">
          {withStrong(t('events.goLive.noSource'), { settingsPath })}
        </p>
      )}

      {feed === 'required' && (
        <div className="go-live-modal-feed" role="alert" data-testid="go-live-feed-required">
          <p className="go-live-modal-error">{t('events.goLive.feedRequired')}</p>
          {onOpenFeedSetup && (
            <button type="button" className="go-live-modal-feed-link" onClick={onOpenFeedSetup} data-testid="go-live-feed-setup">
              {t('events.goLive.feedSetup')}
            </button>
          )}
        </div>
      )}

      {error && (
        <p className="go-live-modal-error" role="alert">{error}</p>
      )}

      <div className="button-group">
        <button type="button" className="btn-save" onClick={() => onGoLive(source)} disabled={isSubmitting || feed === 'required'} data-testid="go-live-confirm">
          <FaBroadcastTower aria-hidden="true" /> {t('events.actions.goLive')}
        </button>
        <button type="button" className="btn-cancel" onClick={onClose}>
          {t('common.actions.cancel')}
        </button>
      </div>
    </Modal>
  );
}

export default GoLiveModal;
